import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedDrilldown } from '../Shared/ManagedDrilldown';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimDataSetAdminModel,
    mapSlimDataSetAdminModel,
    SlimDataSetInputAdminModel,
    mapSlimDataSetInputAdminModel,
    SlimChartAdminModel,
    mapSlimChartAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetDataSetAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsRequest';
import GetDataSetAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelResponse';
import GetDataSetInputAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetInputAdminModelsRequest';
import GetDataSetInputAdminModelResponse from '../../Interfaces/Responses/Administration/GetDataSetInputAdminModelResponse';
import GetChartAdminModelsRequest from '../../Interfaces/Requests/Administration/GetChartAdminModelsRequest';
import GetChartAdminModelsResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';
import AUDDrilldownPassthroughMaps, { ManagedDrilldownInputPassthroughMapEditResult } from './AUDDrilldownPassthroughMaps';

export type DrilldownSourceType = 'dataset' | 'chartDataset' | 'reportItem';

export type ManagedDrilldownEditResult = {
    operation: ManagedAdminChildOperation;
    drilldown: ManagedDrilldown;
};

type AUDDrilldownsProps = {
    sourceType: DrilldownSourceType;

    sourceDataSetID?: number | null;
    sourceChartDataSetID?: number | null;
    sourceReportItemID?: number | null;

    sourceTitle: string;
    sourceSubtitle?: string;

    drilldowns: ManagedDrilldown[];

    initialSelectedDrilldownID?: number;
    initialMode?: ManagedAdminActionMode;

    availableSourceColumns?: string[];

    handleSetDrilldowns: (
        result: ManagedDrilldownEditResult
    ) => void;

    close: () => void;
};

type AUDDrilldownFormValues = {
    ddSourceValue: string;
    ddTargetDataSetID?: number | null;
    ddTargetChartID?: number | null;
    ddTargetDataSetInputID?: number;
};

const AUDDrilldowns: React.FC<AUDDrilldownsProps> = ({ close, ...props }) => {
    const [formAUDDrilldown] = Form.useForm();

    const [ddDataSetsIncludeDisabled] = useState(false);
    const [ddChartsIncludeDisabled] = useState(false);
    const [ddDataSetInputsIncludeDisabled] = useState(false);

    const drilldowns = props.drilldowns ?? [];

    const getInitialDDMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedDrilldown = (): ManagedDrilldown | undefined => {
        if (!props.initialSelectedDrilldownID || props.initialSelectedDrilldownID <= 0) {
            return undefined;
        }

        return drilldowns.find(
            (drilldown) => drilldown.id === props.initialSelectedDrilldownID
        );
    };

    const buildDrilldownNameConstruct = (drilldown: ManagedDrilldown | undefined): string => {
        if (!drilldown) {
            return '';
        }

        const formatDrilldownNameID = (id?: number | null): string => {
            return id && id > 0 ? String(id) : 'new';
        };

        const sourceLabel =
            drilldown.sourceChartDataSetId && drilldown.sourceChartDataSetId > 0
                ? `ChartDataSet: [${formatDrilldownNameID(drilldown.sourceChartDataSetId)}]`
                : `DataSet: [${formatDrilldownNameID(drilldown.sourceDataSetId)}]`;

        const sourceValueLabel = drilldown.sourceValue?.trim()
            ? ` / Source: ${drilldown.sourceValue}`
            : '';

        const targetLabel =
            drilldown.targetChartId && drilldown.targetChartId > 0
                ? `Chart: [${formatDrilldownNameID(drilldown.targetChartId)}]`
                : `DataSet: [${formatDrilldownNameID(drilldown.targetDataSetId)}]`;

        const targetInputLabel =
            drilldown.targetDataSetInputId > 0
                ? ` / Input: [${formatDrilldownNameID(drilldown.targetDataSetInputId)}]`
                : '';

        return `${sourceLabel}${sourceValueLabel} -> ${targetLabel}${targetInputLabel}`;
    };

    const initialSelectedDrilldown = getInitialSelectedDrilldown();
    const initialDDMode = getInitialDDMode();

    const initialDDFormValues = initialDDMode === 'add'
        ? {
            ddSourceValue: '',
            ddTargetDataSetID: undefined,
            ddTargetChartID: undefined,
            ddTargetDataSetInputID: undefined,
        }
        : {
            ddSourceValue: initialSelectedDrilldown?.sourceValue ?? '',
            ddTargetDataSetID: initialSelectedDrilldown?.targetDataSetId ?? undefined,
            ddTargetChartID: initialSelectedDrilldown?.targetChartId ?? undefined,
            ddTargetDataSetInputID: (initialSelectedDrilldown?.targetDataSetInputId ?? 0) > 0
                ? initialSelectedDrilldown?.targetDataSetInputId
                : undefined,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialDDMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialDDMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialDDMode === 'delete');

    const [ddSelectedDrilldownID, setDDSelectedDrilldownID] = useState(initialSelectedDrilldown?.id ?? -1);
    const [ddSelectedDrilldownTitle, setDDSelectedDrilldownTitle] = useState(
        initialSelectedDrilldown ? buildDrilldownNameConstruct(initialSelectedDrilldown) : ''
    );

    const [ddSourceValue, setDDSourceValue] = useState<string>(initialSelectedDrilldown?.sourceValue ?? '');
    const [ddTargetDataSetID, setDDTargetDataSetID] = useState<number | null>(initialSelectedDrilldown?.targetDataSetId ?? null);
    const [ddTargetChartID, setDDTargetChartID] = useState<number | null>(initialSelectedDrilldown?.targetChartId ?? null);
    const [ddTargetDataSetInputID, setDDTargetDataSetInputID] = useState<number>(initialSelectedDrilldown?.targetDataSetInputId ?? 0);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredDDField = (fieldName: string): Promise<void> => {
        const value = formAUDDrilldown.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateDDDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'ddSourceValue':
            case 'ddTargetDataSetInputID':
                return validateRequiredDDField(rule.field);

            case 'ddTargetDataSetID':
            case 'ddTargetChartID':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const { data: dataSets = [], isLoading: isGetDataSetsLoading } = useQuery<GetDataSetAdminModelsResponse[], Error, SlimDataSetAdminModel[]>(
        ['getDataSetAdminModelsRequest', ddDataSetsIncludeDisabled],
        async (): Promise<GetDataSetAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS}`;
                const requestData: GetDataSetAdminModelsRequest = { gdsa_includeDisabled: ddDataSetsIncludeDisabled };
                return await AxiosUtil.get<GetDataSetAdminModelsResponse[], GetDataSetAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDDLoadError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleDDLoadError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error', err);
            },
            select: (response: GetDataSetAdminModelsResponse[]): SlimDataSetAdminModel[] => {
                return response
                    .map(mapSlimDataSetAdminModel)
                    .filter((eachDataSet) => !eachDataSet.deleted);
            },
        }
    );

    const { data: charts = [], isLoading: isGetChartsLoading } = useQuery<GetChartAdminModelsResponse[], Error, SlimChartAdminModel[]>(
        ['getChartAdminModelsRequest', ddChartsIncludeDisabled],
        async (): Promise<GetChartAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CHART_ADMIN_MODELS}`;
                const requestData: GetChartAdminModelsRequest = { gca_includeDisabled: ddChartsIncludeDisabled };
                return await AxiosUtil.get<GetChartAdminModelsResponse[], GetChartAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDDLoadError('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleDDLoadError('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models] Internal Error', err);
            },
            select: (response: GetChartAdminModelsResponse[]): SlimChartAdminModel[] => {
                return response
                    .map(mapSlimChartAdminModel)
                    .filter((eachChart) => !eachChart.deleted);
            },
        }
    );

    const sourceDataSetID = props.sourceDataSetID ?? initialSelectedDrilldown?.sourceDataSetId ?? 0;

    const [showDrilldownInputPassthroughMaps, setShowDrilldownInputPassthroughMaps] = useState(false);

    const selectedDrilldown = drilldowns.find((drilldown) => drilldown.id === ddSelectedDrilldownID);

    const selectedDrilldownInputPassthroughMaps = selectedDrilldown?.drilldownInputPassthroughMaps ?? [];

    const selectedSourceDataSetName = dataSets.find((dataSet) => dataSet.id === sourceDataSetID)?.name ?? props.sourceTitle;

    const selectedTargetChart = charts.find((chart) => chart.id === ddTargetChartID);
    const resolvedTargetDataSetID = ddTargetDataSetID ?? selectedTargetChart?.dataSetId ?? 0;

    const selectedTargetDataSetName = dataSets.find((dataSet) => dataSet.id === resolvedTargetDataSetID)?.name ?? '';

    const { data: targetDataSetInputs = [], isLoading: isGetTargetDataSetInputsLoading } = useQuery<GetDataSetInputAdminModelResponse[], Error, SlimDataSetInputAdminModel[]>(
        ['getTargetDataSetInputAdminModelsRequest', resolvedTargetDataSetID, ddDataSetInputsIncludeDisabled],
        async (): Promise<GetDataSetInputAdminModelResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_INPUT_ADMIN_MODELS}`;
                const requestData: GetDataSetInputAdminModelsRequest = {
                    gdsi_datasetID: resolvedTargetDataSetID,
                    gdsi_includeDisabled: ddDataSetInputsIncludeDisabled,
                };
                return await AxiosUtil.get<GetDataSetInputAdminModelResponse[], GetDataSetInputAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDDLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Target DataSet Input Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: resolvedTargetDataSetID > 0,
            onError: (err: Error) => {
                handleDDLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Target DataSet Input Admin Models] Internal Error', err);
            },
            select: (response: GetDataSetInputAdminModelResponse[]): SlimDataSetInputAdminModel[] => {
                return response.map(mapSlimDataSetInputAdminModel);
            },
        }
    );

    const handleDDSourceValueChange = (value: string) => {
        setDDSourceValue(value);
    };

    const handleDDTargetDataSetIDChange = (value: number | null | undefined) => {
        const nextTargetDataSetID = value && value > 0 ? value : null;

        setDDTargetDataSetID(nextTargetDataSetID);
        setDDTargetChartID(null);
        setDDTargetDataSetInputID(0);

        formAUDDrilldown.setFieldValue('ddTargetChartID', undefined);
        formAUDDrilldown.setFieldValue('ddTargetDataSetInputID', undefined);
    };

    const handleDDTargetChartIDChange = (value: number | null | undefined) => {
        const nextTargetChartID = value && value > 0 ? value : null;
        const nextTargetChart = charts.find((chart) => chart.id === nextTargetChartID);

        setDDTargetChartID(nextTargetChartID);
        setDDTargetDataSetID(nextTargetChart?.dataSetId ?? null);
        setDDTargetDataSetInputID(0);

        formAUDDrilldown.setFieldValue('ddTargetDataSetID', undefined);
        formAUDDrilldown.setFieldValue('ddTargetDataSetInputID', undefined);
    };

    const handleDDTargetDataSetInputIDChange = (value: number | undefined) => {
        setDDTargetDataSetInputID(value ?? 0);
    };

    const buildManagedDrilldownRequest = (values: AUDDrilldownFormValues): ManagedDrilldown => {
        const targetChart = charts.find((chart) => chart.id === values.ddTargetChartID);
        const targetDataSetID = values.ddTargetDataSetID ?? targetChart?.dataSetId ?? ddTargetDataSetID;

        return {
            id: addButtonClicked ? 0 : ddSelectedDrilldownID,
            sourceDataSetId: sourceDataSetID > 0 ? sourceDataSetID : null,
            sourceChartDataSetId: props.sourceChartDataSetID ?? initialSelectedDrilldown?.sourceChartDataSetId ?? null,
            sourceValue: values.ddSourceValue?.trim() ?? '',
            targetDataSetId: targetDataSetID && targetDataSetID > 0 ? targetDataSetID : null,
            targetChartId: values.ddTargetChartID && values.ddTargetChartID > 0 ? values.ddTargetChartID : null,
            targetDataSetInputId: values.ddTargetDataSetInputID ?? 0,
            deleted: delButtonClicked,
            drilldownInputPassthroughMaps: initialSelectedDrilldown?.drilldownInputPassthroughMaps ?? [],
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDDrilldownFormValues>) => {
        console.error('AUDDrilldowns validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Drilldown Validation',
                'Please correct the highlighted Drilldown fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDDrilldownFormValues) => {
        const newManagedDrilldown = buildManagedDrilldownRequest(values);

        props.handleSetDrilldowns({
            operation: getDDOperation(),
            drilldown: newManagedDrilldown,
        });

        close();
    };

    const setDDMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearDDSelection = () => {
        setDDSelectedDrilldownID(-1);
        setDDSelectedDrilldownTitle('');

        setDDSourceValue('');
        setDDTargetDataSetID(null);
        setDDTargetChartID(null);
        setDDTargetDataSetInputID(0);

        setDDMode('add');
        handleDDDrilldownResetForm('blank');
    };

    const handleDDSelectionChange = (value: number) => {
        const selectedDrilldown = drilldowns.find((drilldown) => drilldown.id === value);

        setDDSelectedDrilldownID(value);
        setDDSelectedDrilldownTitle(buildDrilldownNameConstruct(selectedDrilldown));

        setDDMode('add');
        handleDDDrilldownResetForm('blank');
    };

    const setDDBlankForm = () => {
        formAUDDrilldown.setFieldValue('ddSourceValue', '');
        formAUDDrilldown.setFieldValue('ddTargetDataSetID', undefined);
        formAUDDrilldown.setFieldValue('ddTargetChartID', undefined);
        formAUDDrilldown.setFieldValue('ddTargetDataSetInputID', undefined);

        setDDSourceValue('');
        setDDTargetDataSetID(null);
        setDDTargetChartID(null);
        setDDTargetDataSetInputID(0);
    };

    const setDDFormFromSelectedDrilldown = () => {
        const selectedDrilldown = drilldowns.find(
            (drilldown) => drilldown.id === ddSelectedDrilldownID
        );

        formAUDDrilldown.setFieldValue('ddSourceValue', selectedDrilldown?.sourceValue ?? '');
        formAUDDrilldown.setFieldValue('ddTargetDataSetID', selectedDrilldown?.targetDataSetId ?? undefined);
        formAUDDrilldown.setFieldValue('ddTargetChartID', selectedDrilldown?.targetChartId ?? undefined);
        formAUDDrilldown.setFieldValue(
            'ddTargetDataSetInputID',
            (selectedDrilldown?.targetDataSetInputId ?? 0) > 0 ? selectedDrilldown?.targetDataSetInputId : undefined
        );

        setDDSourceValue(selectedDrilldown?.sourceValue ?? '');
        setDDTargetDataSetID(selectedDrilldown?.targetDataSetId ?? null);
        setDDTargetChartID(selectedDrilldown?.targetChartId ?? null);
        setDDTargetDataSetInputID(selectedDrilldown?.targetDataSetInputId ?? 0);
    };

    const getDDOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getDDActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const handleDDDrilldownResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDDBlankForm();
            return;
        }

        setDDFormFromSelectedDrilldown();
    };

    //=================================================================
    //=================================================================
    
    const handleDDAddDrilldown = () => {
        clearDDSelection();
    };

    //=================================================================
    //=================================================================

    const handleDDUpdDrilldown = () => {
        setDDMode('update');
        handleDDDrilldownResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleDDDelDrilldown = () => {
        setDDMode('delete');
        handleDDDrilldownResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onDDCancel = () => {
        clearDDSelection();
    };

    const handleSetDrilldownInputPassthroughMaps = ( result: ManagedDrilldownInputPassthroughMapEditResult ) => {
        if (!selectedDrilldown) {
            return;
        }

        const nextPassthroughMaps =
            result.operation === 'add'
                ? [
                    ...selectedDrilldownInputPassthroughMaps,
                    result.drilldownInputPassthroughMap,
                ]
                : selectedDrilldownInputPassthroughMaps.map((passthroughMap) => {
                    return passthroughMap.id === result.drilldownInputPassthroughMap.id
                        ? result.drilldownInputPassthroughMap
                        : passthroughMap;
                });

        props.handleSetDrilldowns({
            operation: 'update',
            drilldown: {
                ...selectedDrilldown,
                drilldownInputPassthroughMaps: nextPassthroughMaps,
            },
        });
    };

    const handleDDLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message =
            apiError?.response?.data?.message
                ? `${title}: ${apiError.response.data.message}`
                : `${title}: ${String(err)}`;

        console.error(title, err);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Drilldown Load Error',
                message,
                true
            )
        );
    };

    const formatDDDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const buildDrilldownSourcePathConstruct = (): string[] => {
        const sourceLabel =
            props.sourceType === 'chartDataset'
                ? `Chart DataSet: [${formatDDDisplayID(props.sourceChartDataSetID)}]`
                : props.sourceType === 'reportItem'
                    ? `Report Item: [${formatDDDisplayID(props.sourceReportItemID)}]`
                    : `DataSet: [${formatDDDisplayID(props.sourceDataSetID)}]`;
        const sourceTitle = props.sourceTitle?.trim() ? ` ${props.sourceTitle}` : '';
        const sourcePathLines = props.sourceSubtitle?.trim()
            ? props.sourceSubtitle.split(' - ').map((sourcePathLine) => sourcePathLine.trim()).filter((sourcePathLine) => sourcePathLine !== '')
            : [];

        return [
            ...sourcePathLines,
            `${sourceLabel}${sourceTitle}`,
        ];
    };

    const buildDrilldownScopeNote = (): string => {
        if (props.sourceType === 'chartDataset') {
            return 'This editor shows explicit Chart DataSet drilldowns only. If this list is empty, the chart may still drill down at runtime through DataSet-level drilldowns configured on the chart source DataSet.';
        }

        if (props.sourceType === 'reportItem') {
            return 'Report Items do not directly own drilldowns. This view shows drilldowns inferred from the Report Item Chart DataSets or DataSet. To change ownership, edit the drilldown from Manage DataSets or Manage Chart DataSets.';
        }

        return 'This editor shows DataSet-level drilldowns. These drilldowns can also be used by charts at runtime when the chart data comes from this DataSet.';
    };

    const panelStyle: React.CSSProperties = {
        border: '1px solid black',
        borderRadius: '6px',
        minHeight: '205px',
        padding: '8px',
        textAlign: 'left',
    };

    const readOnlyValueStyle: React.CSSProperties = {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    };

    const scopeNoteStyle: React.CSSProperties = {
        backgroundColor: '#fff7d6',
        border: '1px solid #e6c75a',
        borderRadius: '4px',
        color: 'irongray',
        padding: '8px',
        margin: '4px',
        textAlign: 'center',
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: '1px solid black',
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '330px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                onCancel={() => setShowDrilldownInputPassthroughMaps(false)}
                open={showDrilldownInputPassthroughMaps}
                width={1000}
            >
                {selectedDrilldown && (
                    <AUDDrilldownPassthroughMaps
                        drilldownID={selectedDrilldown.id}
                        drilldownTitle={buildDrilldownNameConstruct(selectedDrilldown)}
                        sourceDataSetID={sourceDataSetID}
                        sourceDataSetName={selectedSourceDataSetName}
                        targetDataSetID={resolvedTargetDataSetID}
                        targetDataSetName={selectedTargetDataSetName}
                        drilldownInputPassthroughMaps={selectedDrilldownInputPassthroughMaps}
                        handleSetDrilldownInputPassthroughMaps={handleSetDrilldownInputPassthroughMaps}
                        close={() => setShowDrilldownInputPassthroughMaps(false)}
                    />
                )}
            </ServaModal>
            <Spin spinning={ isGetDataSetsLoading || isGetChartsLoading || isGetTargetDataSetInputsLoading }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Drilldowns</Title>
                    </Text>
                </Space>
                {buildDrilldownSourcePathConstruct().map((sourcePathLine) => (
                    <Space key={sourcePathLine} direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                        <Text italic type='secondary'>{sourcePathLine}</Text>
                    </Space>
                ))}
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Drilldown: [' + formatDDDisplayID(ddSelectedDrilldownID) + '] ' + ddSelectedDrilldownTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Drilldown to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Drilldown'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleDDSelectionChange}
                        >
                            {drilldowns.map(
                                (eachDrilldown: ManagedDrilldown) => {
                                    return (
                                        <Option
                                            key={eachDrilldown.id}
                                            label={buildDrilldownNameConstruct(eachDrilldown)}
                                            value={eachDrilldown.id}
                                        >
                                            {buildDrilldownNameConstruct(eachDrilldown)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div style={scopeNoteStyle}>
                            <Text italic>
                                {buildDrilldownScopeNote()}
                            </Text>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDAddDrilldown}
                        >
                            Add Drilldown
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={ddSelectedDrilldownID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDUpdDrilldown}
                        >
                            Update Drilldown
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={ddSelectedDrilldownID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDDelDrilldown}
                        >
                            Delete Drilldown
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDDrilldown}
                    initialValues={initialDDFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getDDActionColor() }}>
                                    {addButtonClicked ? 'Add Drilldown' :
                                        (updButtonClicked ? 'Update Drilldown: [' + formatDDDisplayID(ddSelectedDrilldownID) + '] ' :
                                            (delButtonClicked ? 'Delete Drilldown: [' + formatDDDisplayID(ddSelectedDrilldownID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getDDActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleDDDrilldownResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={panelStyle}>
                                    <Divider orientation='left' orientationMargin={10} style={{ margin: '0px 0px 8px 0px' }}>
                                        <b><u>SOURCE</u></b>
                                    </Divider>

                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={10}>
                                            <Text strong>Source Type</Text>
                                            <br />
                                            <Text
                                                style={readOnlyValueStyle}
                                                title={
                                                    props.sourceType === 'chartDataset'
                                                        ? 'Chart DataSet'
                                                        : props.sourceType === 'reportItem'
                                                            ? 'Report Item'
                                                            : 'DataSet'
                                                }
                                            >
                                                {props.sourceType === 'chartDataset'
                                                    ? 'Chart DataSet'
                                                    : props.sourceType === 'reportItem'
                                                        ? 'Report Item'
                                                        : 'DataSet'}
                                            </Text>
                                        </Col>
                                        <Col span={14}>
                                            <Text strong>Source Owner</Text>
                                            <br />
                                            <Text
                                                style={readOnlyValueStyle}
                                                title={props.sourceTitle}
                                            >
                                                {props.sourceTitle}
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                                        <Col span={10}>
                                            <Text strong>Source DataSet</Text>
                                            <br />
                                            <Text
                                                style={readOnlyValueStyle}
                                                title={'[' + sourceDataSetID + '] ' + (dataSets.find((dataSet) => dataSet.id === sourceDataSetID)?.name ?? '')}
                                            >
                                                {'[' + sourceDataSetID + '] ' + (dataSets.find((dataSet) => dataSet.id === sourceDataSetID)?.name ?? '')}
                                            </Text>
                                        </Col>
                                        <Col span={14}>
                                            <Text strong>Source Chart DataSet</Text>
                                            <br />
                                            <Text
                                                style={readOnlyValueStyle}
                                                title={
                                                    props.sourceChartDataSetID && props.sourceChartDataSetID > 0
                                                        ? '[' + props.sourceChartDataSetID + ']'
                                                        : '(Not Set)'
                                                }
                                            >
                                                {props.sourceChartDataSetID && props.sourceChartDataSetID > 0
                                                    ? '[' + props.sourceChartDataSetID + ']'
                                                    : '(Not Set)'}
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                                        <Col span={24}>
                                            <Form.Item
                                                name='ddSourceValue'
                                                rules={[
                                                    {
                                                        validator: validateDDDetailsData,
                                                    },
                                                ]}
                                                style={{ marginBottom: '0px' }}
                                            >
                                                <Select
                                                    disabled={delButtonClicked}
                                                    label='Source Value / Column:'
                                                    placeholder='Select Source Value / Column'
                                                    showSearch
                                                    value={ddSourceValue}
                                                    onChange={handleDDSourceValueChange}
                                                >
                                                    {(props.availableSourceColumns ?? []).map(
                                                        (eachSourceColumn: string) => {
                                                            return (
                                                                <Option
                                                                    key={eachSourceColumn}
                                                                    label={eachSourceColumn}
                                                                    value={eachSourceColumn}
                                                                >
                                                                    {eachSourceColumn}
                                                                </Option>
                                                            );
                                                        }
                                                    )}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row' style={{ padding: '10px' }}></Row>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={panelStyle}>
                                    <Divider orientation='left' orientationMargin={10} style={{ margin: '0px 0px 8px 0px' }}>
                                        <b><u>TARGET</u></b>
                                    </Divider>

                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={12}>
                                            <Form.Item
                                                name='ddTargetDataSetID'
                                                style={{ marginBottom: '0px' }}
                                            >
                                                <Select
                                                    disabled={delButtonClicked}
                                                    styles={{ popup: { root: { width: 500 } } }}
                                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                                    label='Target DataSet:'
                                                    placeholder='Select Target DataSet'
                                                    showSearch
                                                    value={ddTargetDataSetID ?? undefined}
                                                    onChange={handleDDTargetDataSetIDChange}
                                                >
                                                    {dataSets.map(
                                                        (eachDataSet: SlimDataSetAdminModel) => {
                                                            return (
                                                                <Option
                                                                    key={eachDataSet.id}
                                                                    label={'[' + eachDataSet.id + '] ' + eachDataSet.name}
                                                                    value={eachDataSet.id}
                                                                >
                                                                    {'[' + eachDataSet.id + '] ' + eachDataSet.name}
                                                                </Option>
                                                            );
                                                        }
                                                    )}
                                                </Select>
                                            </Form.Item>
                                        </Col>

                                        <Col span={12}>
                                            <Form.Item
                                                name='ddTargetChartID'
                                                style={{ marginBottom: '0px' }}
                                            >
                                                <Select
                                                    disabled={delButtonClicked}
                                                    styles={{ popup: { root: { width: 500 } } }}
                                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                                    label='Target Chart:'
                                                    placeholder='Select Target Chart'
                                                    showSearch
                                                    value={ddTargetChartID ?? undefined}
                                                    onChange={handleDDTargetChartIDChange}
                                                >
                                                    {charts.map(
                                                        (eachChart: SlimChartAdminModel) => {
                                                            return (
                                                                <Option
                                                                    key={eachChart.id}
                                                                    label={'[' + eachChart.id + '] ' + eachChart.title}
                                                                    value={eachChart.id}
                                                                >
                                                                    {'[' + eachChart.id + '] ' + eachChart.title}
                                                                </Option>
                                                            );
                                                        }
                                                    )}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                                        <Col span={24}>
                                            <Form.Item
                                                name='ddTargetDataSetInputID'
                                                rules={[
                                                    {
                                                        validator: validateDDDetailsData,
                                                    },
                                                ]}
                                                style={{ marginBottom: '0px' }}
                                            >
                                                <Select
                                                    disabled={delButtonClicked || resolvedTargetDataSetID <= 0}
                                                    styles={{ popup: { root: { width: 500 } } }}
                                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                                    label='Target Input:'
                                                    placeholder='Select Target Input'
                                                    showSearch
                                                    value={ddTargetDataSetInputID > 0 ? ddTargetDataSetInputID : undefined}
                                                    onChange={handleDDTargetDataSetInputIDChange}
                                                >
                                                    {targetDataSetInputs.map(
                                                        (eachDataSetInput: SlimDataSetInputAdminModel) => {
                                                            return (
                                                                <Option
                                                                    key={eachDataSetInput.id}
                                                                    label={'[' + eachDataSetInput.id + '] ' + eachDataSetInput.title}
                                                                    value={eachDataSetInput.id}
                                                                >
                                                                    {'[' + eachDataSetInput.id + '] ' + eachDataSetInput.title}
                                                                </Option>
                                                            );
                                                        }
                                                    )}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '12px' }}>
                                        <Col span={24}>
                                            <Button
                                                disabled={addButtonClicked || ddSelectedDrilldownID <= 0}
                                                style={{
                                                    backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')),
                                                    width: '100%'
                                                }}
                                                type='primary'
                                                onClick={() => setShowDrilldownInputPassthroughMaps(true)}
                                            >
                                                Drilldown Input Passthrough Maps
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='ddCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='ddApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Drilldown' : (updButtonClicked ? 'Update Drilldown' : (delButtonClicked ? 'Delete Drilldown' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='ddCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onDDCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDDrilldowns;
